// ---------------------------------------------------------------------------
// App instalable (PWA): service worker, aviso de "Instalar" y detección de la app instalada.
//
// - registerServiceWorker(): registra public/sw.js. Solo en producción y fuera de la demo
//   (la demo vive en sessionStorage y no debe quedarse guardada como app).
// - Chrome/Edge/Android disparan `beforeinstallprompt` cuando la app se puede instalar: se
//   guarda el evento y useInstallPrompt() ofrece el botón "Instalar" que lo abre.
// - iOS Safari no tiene ese evento: ahí solo se puede explicar el paso
//   (Compartir → Agregar a inicio), por eso useInstallPrompt() devuelve `ios: true`.
// ---------------------------------------------------------------------------
import { useEffect, useState } from 'react';
import { DEMO_MODE } from './demo/flags.js';

let deferredPrompt = null;
const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // sin la mini barra automática de Chrome: lo ofrece nuestro aviso
    deferredPrompt = e;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    notify();
  });
}

export function registerServiceWorker() {
  if (!import.meta.env.PROD || DEMO_MODE || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  });
}

/** Abierta desde la pantalla de inicio (sin barra del navegador). */
export const isStandalone = () =>
  typeof window !== 'undefined' &&
  ((typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches) ||
    window.navigator.standalone === true);

/** iPhone/iPad en Safari (los iPad nuevos se presentan como Mac con pantalla táctil). */
const isIOS = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /iphone|ipad|ipod/i.test(ua) || (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
};

/**
 * Estado del aviso de instalación:
 *  - canPrompt: hay un `beforeinstallprompt` guardado → `install()` abre el diálogo del sistema.
 *  - ios: Safari en iOS sin instalar → mostrar las instrucciones.
 *  - installed: ya corre como app.
 */
export function useInstallPrompt() {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = () => force((n) => n + 1);
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, []);

  const installed = isStandalone();
  return {
    installed,
    canPrompt: !installed && !!deferredPrompt,
    ios: !installed && isIOS(),
    install: async () => {
      const prompt = deferredPrompt;
      if (!prompt) return false;
      deferredPrompt = null;
      prompt.prompt();
      const { outcome } = await prompt.userChoice.catch(() => ({ outcome: 'dismissed' }));
      notify();
      return outcome === 'accepted';
    },
  };
}
